'use client';
import { Alert, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { mergeAttributes, Node } from '@tiptap/core';
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useTranslation } from 'react-i18next';

export interface IframeEmbedOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe_embed: {
      setIframeEmbed: (attrs: {
        src: string;
        width?: string;
        height?: string;
        title?: string;
      }) => ReturnType;
    };
  }
}

function IframeEmbedComponent(props: NodeViewProps) {
  const { node } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const { src = '', width = '100%', height = 'auto', title = '' } = node.attrs;

  if (!src) {
    return (
      <NodeViewWrapper>
        <Alert severity="error">{t('extensions.invalidEmbedNoUrl')}</Alert>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <Box
        sx={{
          my: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        {/* Embed info bar */}
        {title && title !== '' && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: '0 !important' }}>
              {title}
            </Typography>
          </Box>
        )}

        {/* The actual iframe */}
        <Box
          sx={{
            position: 'relative',
            ...(height === 'auto' && {
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
            }),
          }}>
          <iframe
            src={src}
            width={width}
            height={height === 'auto' ? undefined : height}
            title={title || t('extensions.embeddedContent')}
            style={{
              border: 'none',
              display: 'block',
              width: '100%',
              ...(height === 'auto' && {
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
              }),
            }}
            loading="lazy"
          />
        </Box>
      </Box>
    </NodeViewWrapper>
  );
}

// TipTap Node definition
export const IframeEmbed = Node.create<IframeEmbedOptions>({
  name: 'iframe_embed',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: {
        default: '',
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      width: {
        default: '100%',
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => ({ width: attributes.width }),
      },
      height: {
        default: 'auto',
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => ({ height: attributes.height }),
      },
      title: {
        default: '',
        parseHTML: (element) => element.getAttribute('title'),
        renderHTML: (attributes) => {
          if (!attributes.title) return {};
          return { title: attributes.title };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'iframe',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(IframeEmbedComponent);
  },

  addCommands() {
    return {
      setIframeEmbed:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },
});
